import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, MapPin, Users, IndianRupee, Heart, Shield, 
  Award, CheckCircle, Clock, Home, Phone, Mail, Calendar
} from 'lucide-react';
import { getCaregiverById } from '../services/firestoreService';
import { getCaregiverServices } from '../services/servicesService';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const CaregiverPublicProfile = () => {
  const { caregiverId } = useParams();
  const navigate = useNavigate();
  const { reviews } = useData();
  const { user, isFavorite, toggleFavorite } = useAuth();
  const toast = useToast();
  const [savingFavorite, setSavingFavorite] = useState(false);

  const favorited = isFavorite(caregiverId);

  const handleToggleFavorite = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setSavingFavorite(true);
    try {
      const nowFavorited = await toggleFavorite(caregiverId);
      toast?.success?.(nowFavorited ? 'Saved to your favorites' : 'Removed from favorites');
    } catch (err) {
      toast?.error?.('Could not update favorites. Please try again.');
    } finally {
      setSavingFavorite(false);
    }
  };
  
  const [caregiver, setCaregiver] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [startingRate, setStartingRate] = useState(null);

  useEffect(() => {
    loadCaregiverData();
  }, [caregiverId]);

  const loadCaregiverData = async () => {
    try {
      // Load caregiver profile
      const result = await getCaregiverById(caregiverId);
      if (result.success && result.caregiver) {
        // Transform user data to caregiver profile format
        const userData = result.caregiver;
        const caregiverProfile = {
          id: caregiverId,
          user: {
            id: caregiverId,
            name: userData.name || userData.displayName || 'Pet Caregiver',
            avatar: userData.avatar || userData.photoURL || '',
            email: userData.email,
            phone: userData.phone || ''
          },
          title: userData.title || 'Professional Pet Caregiver',
          bio: userData.bio || 'Passionate about pet care and dedicated to providing the best service for your furry friends.',
          rating: userData.rating || 4.5,
          reviewCount: userData.reviewCount || 0,
          homePhotos: userData.homePhotos || [],
          speciesAccepted: userData.speciesAccepted || ['Dog', 'Cat'],
          badges: userData.badges || ['verified'],
          maxPets: userData.maxPets || 3,
          hourlyRate: userData.hourlyRate || 250,
          isBackgroundChecked: userData.isBackgroundChecked || false,
          location: userData.location || '',
          isAvailable: userData.isAvailable !== false
        };
        setCaregiver(caregiverProfile);
        
        // Load caregiver services
        const servicesResult = await getCaregiverServices(caregiverId);
        if (servicesResult.success) {
          const availableServices = servicesResult.services.filter(s => s.available);
          setServices(availableServices);
          
          // Calculate starting rate from services (lowest rate)
          if (availableServices.length > 0) {
            const rates = availableServices.map(s => normalizeToHourly(s.rate, s.rateType));
            const minRate = Math.min(...rates);
            setStartingRate(Math.round(minRate));
          } else {
            setStartingRate(caregiverProfile.hourlyRate);
          }
        } else {
          setStartingRate(caregiverProfile.hourlyRate);
        }
      }
    } catch (error) {
      console.error('Error loading caregiver:', error);
    } finally {
      setLoading(false);
    }
  };

  // Normalize different rate types to hourly for comparison
  const normalizeToHourly = (rate, rateType) => {
    switch (rateType) {
      case 'per hour':
        return rate;
      case 'per day':
        return rate / 8; // Assume 8-hour day
      case 'per night':
        return rate / 10; // Assume 10-hour night
      case 'per walk':
        return rate / 1; // 1-hour walk
      case 'per session':
        return rate / 2; // Assume 2-hour session
      default:
        return rate;
    }
  };

  const caregiverReviews = reviews.filter(r => r.caregiverId === caregiverId);

  const getBadgeConfig = (badge) => {
    const badgeConfigs = {
      'verified': { text: 'Active', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'vetChecked': { text: 'Pet First Aid', color: 'bg-blue-100 text-blue-800', icon: Shield },
      'superhost': { text: 'Top Rated', color: 'bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]', icon: Award },
      'trainer': { text: 'Certified Trainer', color: 'bg-indigo-100 text-indigo-800', icon: Users },
      'catSpecialist': { text: 'Cat Expert', color: 'bg-pink-100 text-pink-800', icon: Heart },
      'daycare': { text: 'Daycare', color: 'bg-orange-100 text-orange-800', icon: Home },
      'grooming': { text: 'Grooming', color: 'bg-cyan-100 text-cyan-800', icon: Star },
      'walker': { text: 'Walker', color: 'bg-lime-100 text-lime-800', icon: MapPin },
      'boarding': { text: 'Boarding', color: 'bg-violet-100 text-violet-800', icon: Home },
      'medical': { text: 'Medical Care', color: 'bg-red-100 text-red-800', icon: Shield },
      'senior-care': { text: 'Senior Care', color: 'bg-slate-100 text-slate-800', icon: Heart },
      'puppy-specialist': { text: 'Puppy Expert', color: 'bg-yellow-100 text-yellow-800', icon: Heart },
      'training': { text: 'Training', color: 'bg-blue-100 text-blue-800', icon: Users }
    };
    
    return badgeConfigs[badge] || { 
      text: badge, 
      color: 'bg-gray-100 text-gray-800',
      icon: Star
    };
  };

  const fallbackAvatar = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face';
  const fallbackImage = 'https://images.unsplash.com/photo-1587559045816-8b0a54d4a55f?w=1200&h=600&fit=crop';

  if (loading) {
    return (
      <div className="pf-page-wrap flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--pf-accent)] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading caregiver profile...</p>
        </div>
      </div>
    );
  }

  if (!caregiver) {
    return (
      <div className="pf-page-wrap flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Caregiver Not Found</h2>
          <p className="text-gray-600 mb-4">This caregiver profile doesn't exist or has been removed.</p>
          <Link to="/search" className="text-[var(--pf-accent)] hover:opacity-80 font-semibold">
            ← Back to Search
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/search" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Search
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-start gap-6">
                <img
                  src={caregiver.user.avatar || fallbackAvatar}
                  alt={caregiver.user.name}
                  className="w-24 h-24 rounded-2xl object-cover border-4 border-white shadow-lg"
                  onError={(e) => e.target.src = fallbackAvatar}
                />
                
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 mb-1">
                        {caregiver.user.name}
                      </h1>
                      <p className="text-lg text-gray-600">{caregiver.title}</p>
                    </div>
                    
                    {caregiver.isBackgroundChecked && (
                      <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                        <Shield className="w-4 h-4" />
                        Background Checked
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                    {caregiver.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {caregiver.location}
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="font-semibold">{caregiver.rating}</span>
                      <span>({caregiver.reviewCount} reviews)</span>
                    </div>
                  </div>

                  {/* Availability Status */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                    caregiver.isAvailable 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    <Clock className="w-4 h-4" />
                    {caregiver.isAvailable ? 'Available Now' : 'Currently Unavailable'}
                  </div>
                </div>
              </div>
            </div>

            {/* About Section */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {caregiver.bio}
              </p>
            </div>

            {/* Badges & Certifications */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Badges & Certifications</h2>
              <div className="flex flex-wrap gap-3">
                {caregiver.badges.map((badge) => {
                  const config = getBadgeConfig(badge);
                  const Icon = config.icon;
                  return (
                    <div 
                      key={badge} 
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium ${config.color}`}
                    >
                      <Icon className="w-4 h-4" />
                      {config.text}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Home Photos */}
            {caregiver.homePhotos.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">My Space</h2>
                
                {/* Main Photo */}
                <div className="mb-4">
                  <img
                    src={caregiver.homePhotos[selectedPhotoIndex] || fallbackImage}
                    alt="Home"
                    className="w-full h-96 object-cover rounded-xl"
                    onError={(e) => e.target.src = fallbackImage}
                  />
                </div>

                {/* Thumbnail Grid */}
                {caregiver.homePhotos.length > 1 && (
                  <div className="grid grid-cols-4 gap-3">
                    {caregiver.homePhotos.map((photo, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedPhotoIndex(index)}
                        className={`relative h-20 rounded-lg overflow-hidden border-2 transition-all ${
                          selectedPhotoIndex === index
                            ? 'border-[var(--pf-accent)]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={photo}
                          alt={`Home ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Services Offered */}
            {services.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Services Offered</h2>
                <div className="space-y-4">
                  {services.map((service) => (
                    <div key={service.id} className="border border-gray-200 rounded-xl p-4 hover:border-[var(--pf-accent)] transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 text-lg">{service.name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                        </div>
                        <div className="text-right ml-4">
                          <p className="text-2xl font-bold text-[var(--pf-accent)]">₹{service.rate}</p>
                          <p className="text-xs text-gray-500">{service.rateType}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>Up to {service.maxPets} pets</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews */}
            {caregiverReviews.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Reviews ({caregiverReviews.length})
                </h2>
                <div className="space-y-4">
                  {caregiverReviews.slice(0, 5).map((review) => (
                    <div key={review.id} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                      <div className="flex items-center gap-3 mb-2">
                        <img
                          src={review.ownerAvatar || fallbackAvatar}
                          alt={review.ownerName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <p className="font-semibold text-gray-900">{review.ownerName}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-3 h-3 ${
                                    i < review.rating
                                      ? 'text-yellow-500 fill-current'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(review.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-gray-700">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sticky top-24">
              {/* Pricing */}
              <div className="mb-6 pb-6 border-b border-gray-100">
                <p className="text-sm text-gray-600 mb-1">Starting from</p>
                <p className="text-3xl font-bold text-gray-900">
                  ₹{startingRate || caregiver.hourlyRate}
                  <span className="text-lg font-normal text-gray-600">/hr</span>
                </p>
                {services.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {services.length} service{services.length > 1 ? 's' : ''} available
                  </p>
                )}
              </div>

              {/* Quick Info */}
              <div className="space-y-3 mb-6 pb-6 border-b border-gray-100">
                <div className="flex items-center gap-3 text-gray-700">
                  <Users className="w-5 h-5 text-[var(--pf-accent)]" />
                  <span>Up to {caregiver.maxPets} pets</span>
                </div>
                {caregiver.location && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <MapPin className="w-5 h-5 text-[var(--pf-accent)]" />
                    <span>{caregiver.location}</span>
                  </div>
                )}
              </div>

              {/* Species Accepted */}
              <div className="mb-6">
                <p className="text-sm font-semibold text-gray-900 mb-3">Pet Species Accepted</p>
                <div className="flex flex-wrap gap-2">
                  {caregiver.speciesAccepted.map((species) => (
                    <span 
                      key={species} 
                      className="px-3 py-1.5 bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] rounded-lg text-sm font-medium"
                    >
                      {species}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <Link
                  to={`/owner-booking/${caregiver.id}`}
                  className="pf-btn-primary w-full py-3 rounded-xl font-semibold text-center block"
                >
                  Book Now
                </Link>
                
                <button
                  onClick={handleToggleFavorite}
                  disabled={savingFavorite}
                  aria-pressed={favorited}
                  className={`w-full py-3 bg-white border-2 rounded-xl font-semibold transition-all text-center flex items-center justify-center gap-2 disabled:opacity-60 ${
                    favorited
                      ? 'border-red-200 text-red-600 hover:bg-red-50'
                      : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${favorited ? 'fill-red-500 text-red-500' : ''}`} />
                  {favorited ? 'Saved to Favorites' : 'Save to Favorites'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaregiverPublicProfile;

